/**
 * B2BBeforeAfter
 * THE PROBLEM  : [left label col] | [growing line + cards]
 * OUR PROMISE  : [left label col] | [growing line + points] | [one unified platform card]
 *
 * Both sections share max-w-6xl so their left labels align perfectly.
 * No red. Slate palette for Problem; sky/slate for Promise.
 * Line is a plain vertical bar — no dots, no branch ticks.
 */

import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';

// ── Copy ──────────────────────────────────────────────────────────────────────

const PROBLEM_ITEMS = [
  { title: 'No single source of truth — data lives in silos, nothing connects' },
  { title: 'Hiring decisions driven by gut feel, not structured evidence' },
  { title: 'Recruiters buried in admin — scheduling, follow-ups, manual screening' },
  { title: 'Disconnected tools with zero shared context or candidate handoff' },
];

const PROMISE_ITEMS = [
  {
    n: '01',
    title: 'AI Fitment Scoring — 1:1 candidate-to-role match and 1:N bulk role-fit matrix across your full pipeline',
  },
  {
    n: '02',
    title: 'Parallel AI interview screening — assess every candidate simultaneously, not one at a time',
  },
  {
    n: '03',
    title: 'Role-based access for every stakeholder — owner, admin, recruiter, and hiring manager, each with the right view',
  },
  {
    n: '04',
    title: 'AI video interviews with auto-generated transcripts, gap analysis, and a structured defensible scorecard',
  },
];

// ── Scroll-driven vertical line ───────────────────────────────────────────────
// Plain 2 px line — no dots, no ticks. Grows as the user scrolls the section.

function TimelineLine({
  sectionRef,
  colorClass,
}: {
  sectionRef: React.RefObject<HTMLDivElement | null>;
  colorClass: string;
}) {
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start 0.85', 'end 0.2'],
  });
  const smooth = useSpring(scrollYProgress, { stiffness: 52, damping: 22 });
  const lineH = useTransform(smooth, [0, 1], ['0%', '100%']);

  return (
    <>
      {/* ghost track */}
      <div
        className="pointer-events-none absolute bottom-0 left-0 top-0 w-0.5 opacity-10"
        style={{ background: 'currentColor' }}
        aria-hidden
      />
      {/* animated fill */}
      <motion.div
        className={`pointer-events-none absolute left-0 top-0 w-0.5 rounded-full ${colorClass}`}
        style={{ height: lineH }}
        aria-hidden
      />
    </>
  );
}

// ── Thin gradient-border card shell ──────────────────────────────────────────

function LineCard({
  gradient,
  children,
}: {
  gradient: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ background: gradient, padding: '1px' }} className="rounded-2xl">
      <div className="rounded-[15px] bg-white px-5 py-4 dark:bg-gray-900">
        {children}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function B2BBeforeAfter() {
  const problemRef = useRef<HTMLDivElement>(null);
  const promiseRef = useRef<HTMLDivElement>(null);

  return (
    <section id="problem" className="scroll-mt-28 bg-transparent py-12 md:py-16">
      {/* ── Section header ─────────────────────────────────────────────────── */}
      <div className="px-4 pb-8 text-center md:pb-10">
        <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.28em] text-slate-400 dark:text-slate-500">
          The GoGrabJob difference
        </p>
        <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-4xl md:text-[2.75rem]">
          Broken hiring process?{' '}
          <span className="text-sky-600 dark:text-sky-400">We cut the time in half.</span>
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-gray-500 dark:text-gray-400">
          Most teams run hiring on disconnected tools, gut decisions, and manual work.
          GoGrabJob replaces that with one AI-powered workspace — built for speed, accuracy, and accountability.
        </p>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          THE PROBLEM  —  max-w-6xl  grid [220px  1fr]
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="mx-auto max-w-6xl px-4 pb-0 sm:px-6">
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] md:gap-12">

          {/* Left label */}
          <div className="hidden md:block">
            <div className="pt-3">
              <span className="inline-block rounded-full bg-slate-100 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                The Problem
              </span>
              <h3 className="mt-4 text-2xl font-extrabold leading-tight tracking-tight text-gray-900 dark:text-white">
                What is slowing
                <br />
                your hiring down
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-500 dark:text-slate-400">
                Disconnected tools, no shared context, decisions made on instinct.
              </p>
              <div className="mt-5 h-px w-10 bg-slate-200 dark:bg-slate-700" />
            </div>
          </div>

          {/* Right: growing line + cards — NO dots, NO branch ticks */}
          <div ref={problemRef} className="relative pl-6" style={{ color: '#94a3b8' }}>
            {/* Mobile heading */}
            <div className="mb-7 md:hidden">
              <span className="inline-block rounded-full bg-slate-100 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-600">
                The Problem
              </span>
              <h3 className="mt-3 text-xl font-extrabold text-gray-900 dark:text-white">
                What is slowing your hiring down
              </h3>
            </div>

            <TimelineLine
              sectionRef={problemRef}
              colorClass="bg-gradient-to-b from-slate-300 via-slate-400 to-slate-300"
            />

            <div className="space-y-5 py-3 pb-10">
              {PROBLEM_ITEMS.map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: false, margin: '-80px 0px' }}
                  transition={{ duration: 0.38, delay: 0.04 + i * 0.05, ease: 'easeOut' }}
                >
                  <LineCard gradient="linear-gradient(135deg,#e2e8f0 0%,#f1f5f9 55%,#f8fafc 100%)">
                    <div className="flex items-start gap-3">
                      <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" strokeWidth={2} aria-hidden />
                      <p className="text-base font-semibold leading-snug text-gray-800 dark:text-white">
                        {item.title}
                      </p>
                    </div>
                  </LineCard>
                </motion.div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          OUR PROMISE  —  same max-w-6xl  grid [220px  1fr  300px]
          Left label column is IDENTICAL width (220px) → perfect vertical alignment
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="mx-auto max-w-6xl px-4 pt-10 sm:px-6 md:pt-12">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-[220px_1fr_300px]">

          {/* Left label — identical 220px column */}
          <div className="hidden md:block">
            <div className="pt-3">
              <span className="inline-block rounded-full bg-sky-100 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-sky-700 dark:bg-sky-900/30 dark:text-sky-400">
                Our Promise
              </span>
              <h3 className="mt-4 text-2xl font-extrabold leading-tight tracking-tight text-gray-900 dark:text-white">
                Features only
                <br />
                GoGrabJob delivers
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-sky-700/80 dark:text-sky-400/70">
                Built-in differentiators your current ATS cannot match.
              </p>
              <div className="mt-5 h-px w-10 bg-sky-200 dark:bg-sky-800" />
            </div>
          </div>

          {/* Centre: growing line + promise points — NO dots, NO branch ticks */}
          <div>
            {/* Mobile heading */}
            <div className="mb-7 md:hidden">
              <span className="inline-block rounded-full bg-sky-100 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-sky-700">
                Our Promise
              </span>
              <h3 className="mt-3 text-xl font-extrabold text-gray-900 dark:text-white">
                Features only GoGrabJob delivers
              </h3>
            </div>

            <div ref={promiseRef} className="relative pl-6" style={{ color: '#0ea5e9' }}>
              <TimelineLine
                sectionRef={promiseRef}
                colorClass="bg-gradient-to-b from-sky-400 via-violet-400 to-emerald-400"
              />

              <div className="space-y-5 py-3 pb-10">
                {PROMISE_ITEMS.map((item, i) => (
                  <motion.div
                    key={item.n}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: false, margin: '-80px 0px' }}
                    transition={{ duration: 0.38, delay: 0.05 + i * 0.06, ease: 'easeOut' }}
                  >
                    <LineCard gradient="linear-gradient(135deg,#bae6fd 0%,#e0f2fe 45%,#f0f9ff 100%)">
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 shrink-0 rounded-lg bg-sky-100 px-2 py-0.5 text-[10px] font-extrabold tabular-nums text-sky-600 dark:bg-sky-900/40 dark:text-sky-400">
                          {item.n}
                        </span>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="mt-0.5 h-4.5 w-4.5 shrink-0 text-sky-400" strokeWidth={2} aria-hidden />
                          <p className="text-base font-semibold leading-snug text-gray-900 dark:text-white">
                            {item.title}
                          </p>
                        </div>
                      </div>
                    </LineCard>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: ONE unified platform simulation card */}
          <div className="hidden md:block">
            <div className="sticky top-8 pt-2">
              <PlatformCard />
            </div>
          </div>

          {/* Mobile: platform card below points */}
          <div className="md:hidden">
            <PlatformCard />
          </div>

        </div>
      </div>
    </section>
  );
}

// ── Unified GoGrabJob platform simulation ─────────────────────────────────────
// One card showing all 4 promise features in a single cohesive mini-dashboard.
// Palette: white bg, slate-50 surfaces, slate-100/200 borders. No strong hues.

const FIT_ROWS = [
  { name: 'Priya S.',  score: 94, mode: '1:1' },
  { name: 'Rajan M.',  score: 87, mode: '1:N' },
  { name: 'Amita K.',  score: 78, mode: '1:N' },
];

const WAVEFORM = [3, 5, 8, 6, 10, 7, 4, 9, 6, 5, 8, 7, 4, 6, 9, 5];

function PlatformCard() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.15 }}
      className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-lg shadow-slate-100/70 dark:border-slate-700/50 dark:bg-slate-900 dark:shadow-none"
    >
      {/* Browser chrome */}
      <div className="flex items-center gap-1.5 border-b border-slate-100 bg-slate-50 px-3 py-2.5 dark:border-slate-700 dark:bg-slate-800">
        <div className="h-2 w-2 rounded-full bg-slate-200 dark:bg-slate-600" />
        <div className="h-2 w-2 rounded-full bg-slate-200 dark:bg-slate-600" />
        <div className="h-2 w-2 rounded-full bg-slate-200 dark:bg-slate-600" />
        <span className="ml-2 text-[9px] font-medium text-slate-400">GoGrabJob · AI Pipeline</span>
      </div>

      <div className="space-y-3 p-3">

        {/* Active role chip */}
        <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 dark:bg-slate-800">
          <div>
            <p className="text-[11px] font-bold text-slate-700 dark:text-slate-200">Sr. Product Designer</p>
            <p className="text-[9px] text-slate-400">12 applicants · 3 shortlisted</p>
          </div>
          <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[8px] font-semibold text-slate-500 dark:bg-slate-700 dark:text-slate-400">
            Active
          </span>
        </div>

        {/* ── 01  Fitment Score ─────────────────────────────────────────────── */}
        <div>
          <p className="mb-1.5 text-[8px] font-bold uppercase tracking-widest text-slate-400">AI Fitment Score</p>
          <div className="space-y-1.5">
            {FIT_ROWS.map((c, i) => (
              <div key={c.name} className="flex items-center gap-2">
                <span className="w-14 shrink-0 text-[9px] text-slate-500 dark:text-slate-400">{c.name}</span>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                  <motion.div
                    className="h-full rounded-full bg-slate-400 dark:bg-slate-400"
                    initial={{ width: '0%' }}
                    whileInView={{ width: `${c.score}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.85, delay: 0.25 + i * 0.1, ease: 'easeOut' }}
                  />
                </div>
                <span className="w-7 text-right text-[9px] font-bold tabular-nums text-slate-600 dark:text-slate-300">
                  {c.score}%
                </span>
                <span className="rounded bg-slate-100 px-1 py-px text-[7px] font-medium text-slate-400 dark:bg-slate-700">
                  {c.mode}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── 02  Parallel Screening ────────────────────────────────────────── */}
        <div>
          <p className="mb-1.5 text-[8px] font-bold uppercase tracking-widest text-slate-400">Parallel Screening</p>
          <div className="flex gap-1.5">
            {['A', 'B', 'C'].map((l, i) => (
              <div key={l} className="flex flex-1 flex-col gap-1 rounded-lg bg-slate-50 px-2 py-1.5 dark:bg-slate-800">
                <span className="text-[8px] font-semibold text-slate-500 dark:text-slate-400">Cand. {l}</span>
                <div className="h-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                  <motion.div
                    className="h-full rounded-full bg-slate-400 dark:bg-slate-500"
                    initial={{ width: '8%' }}
                    whileInView={{ width: ['8%', '55%', '85%', '100%'] }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.8, delay: i * 0.22, ease: 'easeInOut' }}
                  />
                </div>
              </div>
            ))}
          </div>
          <p className="mt-1 text-right text-[8px] text-slate-400">3 running simultaneously</p>
        </div>

        {/* ── 03 + 04  RBAC & Video in one row ─────────────────────────────── */}
        <div className="flex gap-2">

          {/* Role-based access */}
          <div className="flex-1">
            <p className="mb-1.5 text-[8px] font-bold uppercase tracking-widest text-slate-400">Access</p>
            <div className="grid grid-cols-2 gap-1">
              {['Owner', 'Admin', 'Recruiter', 'HM'].map((r) => (
                <span
                  key={r}
                  className="rounded-lg border border-slate-100 bg-slate-50 py-1 text-center text-[8px] font-medium text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
                >
                  {r}
                </span>
              ))}
            </div>
          </div>

          {/* Video + transcript */}
          <div className="flex-1">
            <p className="mb-1.5 text-[8px] font-bold uppercase tracking-widest text-slate-400">Interview</p>
            <div className="rounded-lg border border-slate-100 bg-slate-50 p-2 dark:border-slate-700 dark:bg-slate-800">
              {/* Waveform */}
              <div className="flex h-6 items-end gap-px">
                {WAVEFORM.map((h, i) => (
                  <motion.div
                    key={i}
                    className="flex-1 rounded-sm bg-slate-300 dark:bg-slate-600"
                    style={{ height: `${h * 1.8}px` }}
                    initial={{ scaleY: 0 }}
                    whileInView={{ scaleY: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.25, delay: 0.55 + i * 0.03 }}
                  />
                ))}
              </div>
              <p className="mt-1 text-right text-[7px] font-medium text-slate-400">
                Transcript + Scorecard ✓
              </p>
            </div>
          </div>

        </div>

      </div>
    </motion.div>
  );
}
